import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';
import { Reveal } from './decor';
import { orderApi } from '../lib/orderApi';
import { HeroButtons } from './hero/HeroButtons';
import { HeroStats } from './hero/HeroStats';

const DAY_KEY_BY_ES_NAME = {
  lunes: 'monday', martes: 'tuesday', miércoles: 'wednesday', jueves: 'thursday',
  viernes: 'friday', sábado: 'saturday', domingo: 'sunday',
};

const Hero = () => {
  const { t } = useLanguage();
  const { hero } = mockData;
  const navigate = useNavigate();
  const [orderOpenDayName, setOrderOpenDayName] = useState('');

  useEffect(() => {
    orderApi.get('/delivery/config')
      .then(({ data }) => setOrderOpenDayName(data?.window?.order_open_day_name || ''))
      .catch(() => setOrderOpenDayName(''));
  }, []);

  const dayKey = DAY_KEY_BY_ES_NAME[orderOpenDayName.toLowerCase()];
  const localizedOrderDay = dayKey ? t(`location.days.${dayKey}`) : orderOpenDayName;

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const stats = [
    { value: t('hero.stats.oneValue'), label: t('hero.stats.oneLabel') },
    { value: t('hero.stats.twoValue'), label: t('hero.stats.twoLabel') },
    { value: t('hero.stats.threeValue'), label: t('hero.stats.threeLabel') }
  ];

  return (
    <section
      id="hero"
      className="relative w-full"
      style={{ background: '#0A0A0A', minHeight: '100vh' }}
      data-testid="hero-section"
    >
      <div className="grid lg:grid-cols-2 min-h-screen">
        {/* Left - Text */}
        <div className="flex items-center px-6 lg:px-[60px] pt-28 pb-16 lg:py-0">
          <div className="max-w-[520px]">
            <Reveal>
              <div className="flex items-center" style={{ gap: '14px' }}>
                <span style={{ display: 'block', width: '40px', height: '1px', background: '#C9A84C' }} />
                <span
                  className="font-body uppercase"
                  style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C' }}
                >
                  {t('hero.eyebrow')}
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1
                className="font-display mt-6"
                style={{ fontWeight: 900, lineHeight: 1.02, color: '#F5F5F0' }}
              >
                <span className="block text-5xl sm:text-6xl lg:text-[84px]">{t('hero.mainTitle')}</span>
                <span className="block text-5xl sm:text-6xl lg:text-[84px]" style={{ color: '#C8282B' }}>
                  {t('hero.mainTitleHighlight')}
                </span>
              </h1>
            </Reveal>

            <Reveal delay={0.2}>
              <p
                className="font-body mt-6"
                style={{ fontSize: '15px', fontWeight: 300, color: '#C2C1B6', maxWidth: '400px', lineHeight: 1.8 }}
              >
                {t('hero.description')} {t('hero.descriptionHighlight')}
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <HeroButtons
                t={t}
                onExploreMenu={() => scrollTo('menu')}
                onOrderNow={() => navigate('/pedir')}
                onSeeNews={() => scrollTo('trending')}
                localizedOrderDay={localizedOrderDay}
              />
            </Reveal>

            <Reveal delay={0.4}>
              <HeroStats stats={stats} />
            </Reveal>
          </div>
        </div>

        {/* Right - Image */}
        <div className="relative min-h-[420px] lg:min-h-screen overflow-hidden">
          <img
            src={hero.heroImage}
            alt="Yamauchi Ramen"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }}
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, #0A0A0A 0%, rgba(10,10,10,0.2) 45%, transparent 100%)' }}
          />
          {/* Badge */}
          <div
            className="absolute flex flex-col items-center justify-center text-center"
            style={{
              bottom: '40px',
              right: '40px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              border: '1px solid #C9A84C',
              background: 'rgba(10,10,10,0.65)',
              backdropFilter: 'blur(4px)'
            }}
            data-testid="hero-badge"
          >
            <span style={{ fontSize: '38px', color: '#C9A84C', lineHeight: 1 }}>麵</span>
            <span
              className="font-body uppercase mt-1 px-2"
              style={{ fontSize: '8px', letterSpacing: '1.5px', color: '#C9A84C' }}
            >
              {t('hero.badge')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
